import { 
  Contract, 
  rpc,
  Networks, 
  xdr,
  nativeToScVal,
  Address,
  BASE_FEE,
  TransactionBuilder
} from '@stellar/stellar-sdk';
import { ISO2_TO_NUMERIC } from './countryCodes';

// ISO 3166-1 alpha-2 → numeric map (full list from countryCodes.ts)
const ContractClient_ALPHA2_TO_NUMERIC: Record<string, number> = ISO2_TO_NUMERIC;

/**
 * Read-only contract client that doesn't require a wallet
 * Used for fetching public data like country policies and session info
 */
export class ReadOnlyContractClient {
  private rpc: rpc.Server;
  private network: Networks;

  // V2 contract IDs for routing
  private v2SessionId: string;
  private v2VaultId: string;
  private v2ComplianceId: string;
  private v2CoreId: string;

  // Same routing maps as ContractClient (duplicated here to avoid circular imports)
  private static readonly V2_ROUTING: Record<string, 'session' | 'vault' | 'compliance' | 'core'> = {
    get_session: 'session', get_live_session_id: 'session', get_trust_group: 'session',
    get_anchored_event: 'session', get_last_cell_id: 'session',
    balance: 'vault', total_vault_balance: 'vault', get_passkey: 'vault',
    get_country_policy: 'compliance', get_user_compliance_state: 'compliance', get_kyc_data: 'compliance',
    get_default_allow_all: 'compliance', get_transfer_allowed: 'compliance',
    get_country_info: 'core', get_country_admin: 'core', get_registry: 'core', get_admin: 'core',
    list_allowed_countries: 'core',
  };

  private static readonly V2_RENAMES: Record<string, string> = {
    get_total_vault_balance: 'total_vault_balance',
    get_passkey_pubkey: 'get_passkey',
  };

  private getV2ContractId(functionName: string): string {
    const v2Name = ReadOnlyContractClient.V2_RENAMES[functionName] || functionName;
    const domain = ReadOnlyContractClient.V2_ROUTING[v2Name];
    switch (domain) {
      case 'session':    return this.v2SessionId;
      case 'vault':      return this.v2VaultId;
      case 'compliance': return this.v2ComplianceId;
      case 'core':       return this.v2CoreId;
      default:           return this.v2CoreId; // default to core for unknown functions
    }
  }

  private getV2FunctionName(functionName: string): string {
    return ReadOnlyContractClient.V2_RENAMES[functionName] || functionName;
  }

  constructor() {
    this.network = Networks.TESTNET;
    let rpcUrl = process.env.REACT_APP_SOROBAN_RPC_URL;
    if (!rpcUrl && typeof window !== 'undefined') {
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      rpcUrl = isDevelopment
        ? `${window.location.protocol}//${window.location.hostname}:8080/api/soroban-rpc`
        : `${window.location.protocol}//${window.location.host}/api/soroban-rpc`;
    } else if (!rpcUrl) {
      rpcUrl = 'https://soroban-testnet.stellar.org';
    }
    this.rpc = new rpc.Server(rpcUrl);

    // V2 contract IDs — required
    this.v2SessionId    = process.env.REACT_APP_V2_SESSION_ID    || '';
    this.v2VaultId      = process.env.REACT_APP_V2_VAULT_ID      || '';
    this.v2ComplianceId = process.env.REACT_APP_V2_COMPLIANCE_ID || '';
    this.v2CoreId       = process.env.REACT_APP_V2_CORE_ID       || '';
  }

  private async call(functionName: string, ...args: any[]): Promise<any> {
    try {
      // Try to use backend API first (uses service account for read-only operations)
      // Check if we're in development or if backend is available
      const isDevelopment = typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      
      if (isDevelopment || process.env.REACT_APP_USE_BACKEND_API === 'true') {
        try {
          // Convert args to parameters array for backend (plain values, backend will convert)
          const parameters = args;

          const backendUrl = isDevelopment 
            ? `${window.location.protocol}//${window.location.hostname}:8080/api/contract/readonly`
            : '/api/contract/readonly';

          const v2ContractId   = this.getV2ContractId(functionName);
          const v2FunctionName = this.getV2FunctionName(functionName);
          const response = await fetch(backendUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              contractId: v2ContractId,
              functionName: v2FunctionName,
              parameters,
            }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.result) {
              // Parse base64 XDR back to ScVal
              const scVal = xdr.ScVal.fromXDR(data.result, 'base64');
              // Convert ScVal to JavaScript
              return this.scValToJs(scVal);
            }
            return null;
          }
          // If backend fails, fall through to direct RPC
          // console.warn('[ReadOnlyContractClient] Backend API failed, falling back to direct RPC');
        } catch (backendError) {
          // If backend is not available, fall through to direct RPC
          // console.warn('[ReadOnlyContractClient] Backend API not available, using direct RPC:', backendError);
        }
      }

      // Fallback: Direct RPC simulation (original implementation)
      // Convert arguments to ScVal (same logic as ContractClient)
      const scValArgs = args.map(arg => {
        if (arg instanceof xdr.ScVal) {
          return arg;
        }
        if (typeof arg === 'number') {
          return xdr.ScVal.scvU32(arg);
        }
        if (typeof arg === 'boolean') {
          return xdr.ScVal.scvBool(arg);
        }
        if (typeof arg === 'string') {
          if (arg.length === 56 && (arg.startsWith('G') || arg.startsWith('C'))) {
            try {
              const addr = Address.fromString(arg);
              return addr.toScVal();
            } catch {
              return xdr.ScVal.scvString(arg);
            }
          }
          return xdr.ScVal.scvString(arg);
        }
        if (arg instanceof Uint8Array) {
          return nativeToScVal(arg);
        }
        if (typeof Buffer !== 'undefined' && Buffer.isBuffer(arg)) {
          return nativeToScVal(new Uint8Array(arg));
        }
        if (arg === null || arg === undefined) {
          return xdr.ScVal.scvVoid();
        }
        return arg;
      });

      // Build contract operation — route to the correct V2 contract
      const v2ContractId   = this.getV2ContractId(functionName);
      const v2FunctionName = this.getV2FunctionName(functionName);
      const contractOp = new Contract(v2ContractId).call(v2FunctionName, ...scValArgs);
      
      // Create a dummy source account for simulation (we don't need a real account)
      // Use a dummy account ID - simulation doesn't require a real account
      const dummyAccount = {
        accountId: () => 'GAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWHF',
        sequenceNumber: () => '0',
        incrementSequenceNumber: () => {},
      } as any;

      // Build transaction for simulation
      const transaction = new TransactionBuilder(dummyAccount, {
        fee: BASE_FEE,
        networkPassphrase: this.network,
      })
        .addOperation(contractOp)
        .setTimeout(300)
        .build();

      // Simulate the transaction (read-only, no signing needed)
      let simResponse: rpc.Api.SimulateTransactionResponse;
      try {
        simResponse = await this.rpc.simulateTransaction(transaction);
      } catch (simError: any) {
        // Check if it's an XDR parsing error during simulation
        // This can happen when the SDK tries to parse the response and encounters an Option type
        if (simError.message?.includes('Bad union switch') || 
            simError.message?.includes('union switch') ||
            simError.message?.includes('XDR') ||
            simError.toString().includes('Bad union switch')) {
          // console.warn(`[ReadOnlyContractClient] XDR parsing error during simulation for ${functionName} - contract format may have changed or SDK version mismatch:`, simError.message || simError.toString());
          // Return null gracefully to allow app to continue
          return null;
        }
        throw simError;
      }

      if (rpc.Api.isSimulationError(simResponse)) {
        throw new Error(`Simulation failed: ${JSON.stringify(simResponse)}`);
      }

      if (!simResponse.result) {
        throw new Error('No result from simulation');
      }

      // Extract return value
      const retval = simResponse.result.retval;
      if (!retval) {
        return null;
      }

      // Convert ScVal to JavaScript value
      try {
        return this.scValToJs(retval);
      } catch (parseError: any) {
        // If parsing fails with "Bad union switch", the contract might have changed
        // or there's an SDK version mismatch. Return null gracefully.
        if (parseError.message?.includes('Bad union switch') || 
            parseError.message?.includes('union switch') ||
            parseError.message?.includes('XDR') ||
            parseError.toString().includes('Bad union switch')) {
          // console.warn(`[ReadOnlyContractClient] XDR parsing error for ${functionName} - contract format may have changed:`, parseError.message || parseError.toString());
          // Return null gracefully to allow app to continue
          return null;
        }
        throw parseError;
      }
    } catch (error: any) {
      const errorMessage = error.message || error.toString() || '';
      const errorString = String(error);
      
      // Check if it's an XDR parsing error - catch any remaining XDR errors
      if (errorMessage.includes('Bad union switch') || 
          errorMessage.includes('union switch') || 
          errorMessage.includes('XDR_PARSING_ERROR') ||
          errorMessage.includes('XDR') ||
          errorString.includes('Bad union switch')) {
        // console.warn(`[ReadOnlyContractClient] XDR parsing error for ${functionName} - contract format may have changed or SDK version mismatch:`, errorMessage);
        return null; // Return null instead of throwing to allow app to continue
      }
      
      // Check for UnexpectedSize errors (corrupted session data)
      if (errorMessage.includes('UnexpectedSize') || 
          errorMessage.includes('map_unpack_to_linear_memory') ||
          errorMessage.includes('differing host map and output slice lengths') ||
          errorString.includes('UnexpectedSize') ||
          errorString.includes('map_unpack_to_linear_memory')) {
        // console.warn(`[ReadOnlyContractClient] UnexpectedSize error for ${functionName} - session data may be corrupted:`, errorMessage);
        return null; // Return null instead of throwing to allow app to continue
      }
      
      // console.error(`[ReadOnlyContractClient] Error calling ${functionName}:`, error);
      throw error;
    }
  }

  private scValToJs(scVal: xdr.ScVal): any {
    switch (scVal.type) {
      case 'scvBool':
        return scVal.b;
      case 'scvVoid':
        return null;
      case 'scvU32':
        return scVal.u32;
      case 'scvI32':
        return scVal.i32;
      case 'scvU64':
        return scVal.u64.toString();
      case 'scvI64':
        return scVal.i64.toString();
      case 'scvU128': {
        const u128 = scVal.u128;
        return { hi: u128.hi.toString(), lo: u128.lo.toString() };
      }
      case 'scvI128': {
        const i128 = scVal.i128;
        return { hi: i128.hi.toString(), lo: i128.lo.toString() };
      }
      case 'scvString':
        return scVal.str.toString();
      case 'scvSymbol':
        return scVal.sym.toString();
      case 'scvBytes':
        // ScBytes wraps the payload rather than being a Uint8Array itself.
        return scVal.bytes.toBytes();
      case 'scvAddress':
        try {
          return Address.fromScAddress(scVal.address).toString();
        } catch (e) {
          // console.error('[ReadOnlyContractClient] Error converting address:', e);
          return null;
        }
      case 'scvVec': {
        const vec = scVal.vec?.map((v) => this.scValToJs(v)) || [];
        // Special handling for SessionState enum variants which come as Vec<Symbol>
        if (vec.length === 1 && typeof vec[0] === 'string') {
          const symbolString = vec[0];
          // Check if it matches known session states (case-insensitive)
          if (['Waiting', 'Active', 'Ended'].some(s => s.toLowerCase() === symbolString.toLowerCase())) {
            return symbolString; // Return the string representation of the state
          }
        }
        return vec;
      }
      case 'scvMap': {
        const result: any = {};
        scVal.map?.forEach((entry) => {
          // Symbol keys are used for struct fields
          const key = entry.key.type === 'scvSymbol'
            ? entry.key.sym.toString()
            : String(this.scValToJs(entry.key));
          result[key] = this.scValToJs(entry.val);
        });
        return result;
      }
      default:
        // console.warn('[ReadOnlyContractClient] Unhandled ScVal type:', scVal.type);
        return scVal;
    }
  }

  async getCountryPolicy(countryCode: number = 840): Promise<[boolean, number, number] | null> {
    try {
      // Read the real DefaultAllowAll flag from the compliance contract
      const defaultAllowAll = await this.getDefaultAllowAll();
      // V2: get_country_policy(country: u32) -> Option<CountryPolicyV2>
      const result = await this.call('get_country_policy', countryCode);
      const policy = (result && typeof result === 'object') ? result as Record<string, any> : {};
      // i128 fields come back as { hi, lo } — reconstruct as number
      const fromI128 = (v: any): number => {
        if (!v) return 0;
        if (typeof v === 'number') return v;
        if (typeof v === 'object' && 'lo' in v) return Number(v.hi) === 0 ? Number(v.lo) : Number(v.hi) * Math.pow(2, 64) + Number(v.lo);
        return Number(v) || 0;
      };
      const dailyLimit   = fromI128(policy.daily_limit);
      const monthlyLimit = fromI128(policy.monthly_limit);
      return [defaultAllowAll, dailyLimit, monthlyLimit];
    } catch {
      return null; // can't read policy — keep existing app state
    }
  }

  /** Reads the global DefaultAllowAll flag directly from the compliance contract. */
  async getDefaultAllowAll(): Promise<boolean> {
    try {
      const result = await this.call('get_default_allow_all');
      return Boolean(result);
    } catch {
      return false;
    }
  }

  async getCountryAllowed(country: number): Promise<boolean> {
    try {
      // V2 has no get_country_allowed; read the enabled flag from CountryInfoV2 instead.
      const result = await this.call('get_country_info', country);
      if (!result || typeof result !== 'object') return false;
      return (result as any).enabled === true;
    } catch {
      return false;
    }
  }

  async getCountryAdmin(country: number): Promise<string | null> {
    try {
      const result = await this.call('get_country_admin', country);
      if (!result || result === null || result === undefined) {
        return null;
      }
      if (typeof result === 'string') {
        return result;
      }
      return String(result);
    } catch (error: any) {
      // console.error('[ReadOnlyContractClient] Failed to get country admin:', error);
      return null;
    }
  }

  async getSession(sessionId: number): Promise<any> {
    return await this.call('get_session', sessionId);
  }

  async listAllowedCountries(page: number = 0, pageSize: number = 200): Promise<number[]> {
    try {
      // V2 core contract now has list_allowed_countries(page: u32, page_size: u32) -> Vec<u32>
      const result = await this.call('list_allowed_countries', page, pageSize);
      if (Array.isArray(result)) {
        return result.filter((v: any) => typeof v === 'number') as number[];
      }
      return [];
    } catch {
      return [];
    }
  }

  async getAdmin(): Promise<string | null> {
    try {
      const result = await this.call('get_admin');
      if (!result || result === null || result === undefined) {
        return null;
      }
      if (typeof result === 'string') {
        return result;
      }
      return String(result);
    } catch (error: any) {
      // console.error('[ReadOnlyContractClient] Failed to get admin:', error);
      return null;
    }
  }

  async getLiveSessionId(): Promise<number | null> {
    try {
      const result = await this.call('get_live_session_id');
      return result as number | null;
    } catch (error) {
      // console.warn('[ReadOnlyContractClient] Failed to get live session ID:', error);
      return null;
    }
  }

  // Cache so repeated mouse-hover events never re-hit the backend for the same country
  private countryInfoCache = new Map<string, any>();

  async getCountryInfo(countryCode: string): Promise<any> {
    const key = countryCode?.toUpperCase() ?? '';
    // Only serve from cache when we have a real (non-null) result.
    // A null means "not registered yet" — that's transient, so we never cache it.
    if (this.countryInfoCache.has(key)) {
      return this.countryInfoCache.get(key);
    }
    // Deduplicate concurrent in-flight requests for the same country
    if (this.countryInfoInFlight.has(key)) {
      return this.countryInfoInFlight.get(key);
    }
    const promise = (async () => {
      try {
        // V2 get_country_info(numeric_code: u32) — convert alpha-2 string to numeric
        const numericCode = ContractClient_ALPHA2_TO_NUMERIC[key] ?? Number(countryCode) ?? 0;
        if (!numericCode) return null;
        const result = await this.call('get_country_info', numericCode);
        // Only cache a real result — null means "not registered yet" and must not be
        // stored because the country may be registered on the very next call.
        if (result !== null && result !== undefined) {
          this.countryInfoCache.set(key, result);
        }
        return result;
      } catch {
        return null;
      } finally {
        this.countryInfoInFlight.delete(key);
      }
    })();
    this.countryInfoInFlight.set(key, promise);
    return promise;
  }

  /** Evict a cached country-info entry so the next call fetches fresh data. */
  clearCountryInfoCache(iso2: string): void {
    const key = iso2?.toUpperCase() ?? '';
    this.countryInfoCache.delete(key);
    this.countryInfoInFlight.delete(key);
  }

  private countryInfoInFlight = new Map<string, Promise<any>>();

  // ========== Trust Group Functions (Read-Only) ==========

  async getTrustGroup(groupId: number): Promise<any> {
    try {
      const result = await this.call('get_trust_group', groupId);
      return result;
    } catch (error) {
      // console.error('[ReadOnlyContractClient] Failed to get trust group:', error);
      return null;
    }
  }

  /**
   * GeoSession V2 has no `list_my_trust_groups` export — discover groups by scanning
   * `get_trust_group(1..maxId)` and keeping ids where the wallet is owner or member.
   * Read-only RPC (no wallet signature).
   */
  async listTrustGroupIdsForWallet(walletAddress: string, maxId = 256, concurrency = 16): Promise<number[]> {
    if (!walletAddress?.trim()) return [];
    const wallet = walletAddress.trim();

    const inGroup = (group: any): boolean => {
      if (!group) return false;
      const owner = group.owner ?? group.admin;
      if (owner && String(owner) === wallet) return true;
      const mem = group.members;
      if (!Array.isArray(mem)) return false;
      return mem.some((m: any) => String(m) === wallet);
    };

    const ids: number[] = [];
    for (let start = 1; start <= maxId; start += concurrency) {
      const end = Math.min(start + concurrency - 1, maxId);
      const slice: number[] = [];
      for (let id = start; id <= end; id++) slice.push(id);
      const rows = await Promise.all(
        slice.map(async (id) => ({ id, group: await this.getTrustGroup(id) })),
      );
      for (const { id, group } of rows) {
        if (inGroup(group)) ids.push(id);
      }
    }
    ids.sort((a, b) => a - b);
    return ids;
  }

  /**
   * Get the verifier contract address
   */
  async getVerifier(): Promise<string | null> {
    return this.getFromRegistry('zk_verifier');
  }

  async getWebauthnVerifier(): Promise<string | null> {
    return this.getFromRegistry('webauthn_verifier');
  }

  private async getFromRegistry(field: string): Promise<string | null> {
    try {
      // V2 core get_registry() returns ContractRegistry { vault, compliance, zk_verifier, webauthn_verifier, game_hub }
      const registry = await this.call('get_registry');
      if (!registry || typeof registry !== 'object') return null;
      const val = (registry as Record<string, any>)[field];
      if (!val) return null;
      return typeof val === 'string' ? val : String(val);
    } catch {
      return null;
    }
  }

  /**
   * Check if passkey is registered for a user address
   */
  async isPasskeyRegistered(userAddress: string): Promise<boolean> {
    try {
      const result = await this.call('get_passkey_pubkey', userAddress);
      return result !== null && result !== undefined;
    } catch (error) {
      // console.error('[ReadOnlyContractClient] Failed to check passkey registration:', error);
      return false;
    }
  }

  /**
   * Get total vault balance for a country and asset (sum of all user balances)
   * Can be called without wallet connection
   */
  async getTotalVaultBalance(countryCode: string, asset: string): Promise<number> {
    try {
      // V2 total_vault_balance(country: u32, asset: Address) — convert alpha-2 to numeric
      const numericCountry = ContractClient_ALPHA2_TO_NUMERIC[countryCode?.toUpperCase()] ?? Number(countryCode) ?? 0;
      const result = await this.call('get_total_vault_balance', numericCountry, asset);
      if (typeof result === 'number') {
        return result;
      }
      if (typeof result === 'bigint') {
        return Number(result);
      }
      if (result && typeof result === 'object' && 'lo' in result) {
        return Number(result.lo || 0);
      }
      return 0;
    } catch (error) {
      // console.error('[ReadOnlyContractClient] Failed to get total vault balance:', error);
      return 0;
    }
  }

  /**
   * Get global fee config
   * V2: no standalone get_global_fee_config — fees live inside CountryPolicyV2 per country.
   */
  async getGlobalFeeConfig(): Promise<{ fee_percentage: number; country_admin_share: number } | null> {
    // V2 has no global fee config endpoint; return null so UI shows default/empty values.
    return null;
  }

  // ========== KYC/AML Functions (SEP-0009) ==========

  /**
   * Get KYC data for a user in a country (read-only)
   * @param userAddress User's Stellar address
   * @param countryCode ISO 3166-1 alpha-2 country code
   */
  async getKYCData(userAddress: string, countryCode: string): Promise<{
    familyName?: string;
    givenName?: string;
    additionalName?: string;
    addressCountryCode?: string;
    stateOrProvince?: string;
    city?: string;
    postalCode?: string;
    address?: string;
    mobileNumber?: string;
    emailAddress?: string;
    birthDate?: string;
    birthPlace?: string;
    birthCountryCode?: string;
    taxId?: string;
    taxIdName?: string;
    occupation?: number;
    employerName?: string;
    employerAddress?: string;
    languageCode?: string;
    idType?: string;
    idCountryCode?: string;
    idNumber?: string;
    idIssueDate?: string;
    idExpirationDate?: string;
    sex?: string;
    referralId?: string;
    kycStatus: string;
    kycVerifiedAt?: number;
    kycVerifiedBy?: string;
  } | null> {
    try {
      const result = await this.call('get_kyc_data', userAddress, countryCode);
      if (!result || result === null || result === undefined) {
        return null;
      }
      
      // Convert from contract format to frontend format
      return {
        familyName: result.family_name || undefined,
        givenName: result.given_name || undefined,
        additionalName: result.additional_name || undefined,
        addressCountryCode: result.address_country_code || undefined,
        stateOrProvince: result.state_or_province || undefined,
        city: result.city || undefined,
        postalCode: result.postal_code || undefined,
        address: result.address || undefined,
        mobileNumber: result.mobile_number || undefined,
        emailAddress: result.email_address || undefined,
        birthDate: result.birth_date || undefined,
        birthPlace: result.birth_place || undefined,
        birthCountryCode: result.birth_country_code || undefined,
        taxId: result.tax_id || undefined,
        taxIdName: result.tax_id_name || undefined,
        occupation: result.occupation || undefined,
        employerName: result.employer_name || undefined,
        employerAddress: result.employer_address || undefined,
        languageCode: result.language_code || undefined,
        idType: result.id_type || undefined,
        idCountryCode: result.id_country_code || undefined,
        idNumber: result.id_number || undefined,
        idIssueDate: result.id_issue_date || undefined,
        idExpirationDate: result.id_expiration_date || undefined,
        sex: result.sex || undefined,
        referralId: result.referral_id || undefined,
        kycStatus: result.kyc_status || 'pending',
        kycVerifiedAt: result.kyc_verified_at || undefined,
        kycVerifiedBy: result.kyc_verified_by || undefined
      };
    } catch (error) {
      // console.error('[ReadOnlyContractClient] Failed to get KYC data:', error);
      return null;
    }
  }

  /**
   * Check if user has approved KYC for a country (read-only)
   * @param userAddress User's Stellar address
   * @param countryCode ISO 3166-1 alpha-2 country code
   */
  async hasApprovedKYC(userAddress: string, countryCode: string): Promise<boolean> {
    try {
      const result = await this.call('has_approved_kyc', userAddress, countryCode);
      return result === true;
    } catch (error) {
      // console.error('[ReadOnlyContractClient] Failed to check KYC status:', error);
      return false;
    }
  }

  /**
   * Get transfer policy between two countries
   */
  async getTransferAllowed(
    fromCountryCode: string,
    toCountryCode: string
  ): Promise<boolean> {
    try {
      const fromNumeric = ContractClient_ALPHA2_TO_NUMERIC[fromCountryCode.toUpperCase()];
      const toNumeric   = ContractClient_ALPHA2_TO_NUMERIC[toCountryCode.toUpperCase()];
      if (!fromNumeric || !toNumeric) return true; // unknown code → default allow
      const result = await this.call('get_transfer_allowed', fromNumeric, toNumeric);
      // No explicit deny stored = returns true (default allow)
      return result !== false;
    } catch {
      return true; // default allow on error
    }
  }

  /**
   * List all countries that allow transfers from a given country.
   * The compliance contract has no dedicated list function — we check each
   * country individually using get_transfer_allowed.
   * Callers that need this should call getTransferAllowed per-country instead.
   */
  async listAllowedTransferCountries(
    _fromCountryCode: string
  ): Promise<string[]> {
    // No dedicated list endpoint on-chain; callers use per-country getTransferAllowed.
    return [];
  }

  /**
   * Get compliance configuration for a country
   */
  async getComplianceConfig(
    countryCode: string
  ): Promise<{
    kyc_required: boolean;
    daily_limit: number;
    monthly_limit: number;
    sanctions_screened: boolean;
    restricted: boolean;
    min_transaction_amount: number;
    max_transaction_amount: number;
  } | null> {
    try {
      const numericCode = typeof countryCode === 'number' ? countryCode
        : (ContractClient_ALPHA2_TO_NUMERIC[String(countryCode).toUpperCase()] || parseInt(String(countryCode), 10) || 840);
      const result = await this.call('get_country_policy', numericCode);
      if (result && typeof result === 'object') {
        return {
          kyc_required: Boolean((result as any).kyc_required),
          daily_limit: Number((result as any).daily_limit || 0),
          monthly_limit: Number((result as any).monthly_limit || 0),
          sanctions_screened: Boolean((result as any).sanctions_screened),
          restricted: Boolean((result as any).restricted),
          min_transaction_amount: Number((result as any).min_tx_amount || 0),
          max_transaction_amount: Number((result as any).max_tx_amount || 0)
        };
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /** Returns the full CountryPolicyV2 struct for a country, or null if none set. */
  async getFullCountryPolicy(countryNumeric: number): Promise<{
    deposits_enabled: boolean;
    withdrawals_enabled: boolean;
    transfers_in_enabled: boolean;
    transfers_out_enabled: boolean;
    kyc_required: boolean;
    sanctions_screened: boolean;
    restricted: boolean;
    min_tx_amount: number;
    max_tx_amount: number;
    daily_limit: number;
    monthly_limit: number;
    fee_bps: number;
    country_admin_share_bps: number;
    require_webauthn_withdraw: boolean;
    require_zk_for_session_join: boolean;
    allow_public_sessions: boolean;
    policy_version: number;
  } | null> {
    try {
      const result = await this.call('get_country_policy', countryNumeric);
      if (!result || typeof result !== 'object') return null;
      const p = result as Record<string, any>;

      // i128 fields come back as { hi: string, lo: string } from scValToJs.
      // Reconstruct the number: value = hi * 2^64 + lo  (all policy values fit in a JS number)
      const fromI128 = (v: any): number => {
        if (v === null || v === undefined) return 0;
        if (typeof v === 'number') return v;
        if (typeof v === 'string') return Number(v);
        if (typeof v === 'object' && 'lo' in v) {
          const lo = Number(v.lo);
          const hi = Number(v.hi);
          return hi === 0 ? lo : hi * Math.pow(2, 64) + lo;
        }
        return 0;
      };

      return {
        deposits_enabled: Boolean(p.deposits_enabled),
        withdrawals_enabled: Boolean(p.withdrawals_enabled),
        transfers_in_enabled: Boolean(p.transfers_in_enabled),
        transfers_out_enabled: Boolean(p.transfers_out_enabled),
        kyc_required: Boolean(p.kyc_required),
        sanctions_screened: Boolean(p.sanctions_screened),
        restricted: Boolean(p.restricted),
        min_tx_amount: fromI128(p.min_tx_amount),
        max_tx_amount: fromI128(p.max_tx_amount),
        daily_limit: fromI128(p.daily_limit),
        monthly_limit: fromI128(p.monthly_limit),
        fee_bps: fromI128(p.fee_bps),
        country_admin_share_bps: fromI128(p.country_admin_share_bps),
        require_webauthn_withdraw: Boolean(p.require_webauthn_withdraw),
        require_zk_for_session_join: Boolean(p.require_zk_for_session_join),
        allow_public_sessions: Boolean(p.allow_public_sessions),
        policy_version: Number(p.policy_version || 1),
      };
    } catch {
      return null;
    }
  }
}
